// hydration.effects.ts
import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core'
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects'
import { Action, Store } from '@ngrx/store'
import { switchMap, tap } from 'rxjs/operators'
import * as HydrationActions from './hydration.actions'
import { isPlatformBrowser } from '@angular/common'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import * as localforage from 'localforage'
import { IDBFactory } from 'fake-indexeddb'
import { PromiseUtils } from 'libs/shared/utils/helpers/src/lib/promise-utils'
import { gbDebounceAfterFirst } from 'libs/shared/utils/operators/src'

@Injectable()
export class HydrationEffects implements OnInitEffects {
  storage!: any
  INDEX_DB_VERSION_KEY = '%indexDBVersion'
  skipURLPaths = ['under-scheduled-maintenance', 'not-found', 'notfound', 'v', 's']

  get primaryKey(): string {
    return this.getLocalDBKey()
  }

  constructor(
    private action$: Actions,
    private store: Store<{}>,
    @Inject(PLATFORM_ID) protected platformId: InjectionToken<object>,
    @Inject(APP_CONFIG) protected appConfig: any,
  ) {
    const realStorage = localforage.createInstance({
      name: `galabid_${this.appConfig.app}`,
      storeName: 'app', // Should be alphanumeric, with underscores.
      description: 'GalaBid Front browser cache store',
      driver: [localforage.INDEXEDDB, localforage.WEBSQL, localforage.LOCALSTORAGE],
    })

    const fakeStorage = new IDBFactory()

    this.storage = isPlatformBrowser(this.platformId) ? realStorage : fakeStorage
  }

  /**
   * @todo: implement some inheritance for each project and avoid this horrible list
   */
  CMS_URLS: string[] = [
    'localhost',
    'dev-dashboard.galabid.com',
    'staging-dashboard.galabid.com',
    'dashboard.galabid.com',
    'newdashboard-dev.galabid.com',
  ]
  APP_URLS: string[] = ['localhost', 'dev.galabid.com', 'staging.galabid.com', 'newgb.galabid.com', 'app.galabid.com']

  PRESENTER_URLS: string[] = [
    'localhost',
    'dev-presenter.galabid.com',
    'staging-presenter.galabid.com',
    'presenter.galabid.com',
  ]

  hydrate$ = createEffect(() =>
    this.action$.pipe(
      ofType(HydrationActions.hydrate),
      switchMap(() => {
        return this.storage.getItem(this.INDEX_DB_VERSION_KEY).then((version: number) => {
          // check if stored version doesn't match current version
          if (version !== this.appConfig.indexDBVersion) {
            // clear storage
            console.info('clear storage')
            this.storage.clear()
            // store current version number
            this.storage.setItem(this.INDEX_DB_VERSION_KEY, this.appConfig.indexDBVersion)
          }
        })
      }),
      switchMap(() => {
        // getting the state from indexedDB before the hydration
        return Promise.race([
          this.storage.getItem(this.primaryKey),
          // reject if promise does not complete after 5 seconds
          PromiseUtils.timeoutPromise(5000),
        ])
          .then((indexDBState: {}) => indexDBState)
          .catch((error: any) => {
            console.error(error)
            return null
          })
      }),
      switchMap((indexDBState) => {
        const actions: Action[] = []
        const localDBKey = this.getLocalDBKey()

        if (indexDBState && !this.skipURLPaths.includes(localDBKey)) {
          try {
            const state = JSON.parse(String(indexDBState))
            actions.push(HydrationActions.hydrateSuccess(state))
          } catch (error) {
            actions.push(HydrationActions.hydrateFailure(), HydrationActions.removeState())
          }
        } else {
          actions.push(HydrationActions.hydrateFailure())
        }
        return actions
      }),
    ),
  )

  serialize$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(HydrationActions.hydrateSuccess, HydrationActions.hydrateFailure),
        switchMap(() => this.store),
        gbDebounceAfterFirst(1000),
        tap((state) => {
          const localDBKey = this.getLocalDBKey()
          if (!this.skipURLPaths.includes(localDBKey) && !!localDBKey && state) {
            this.storage.setItem(this.primaryKey, JSON.stringify(state))
          }
        }),
      ),
    { dispatch: false },
  )

  removeState$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(HydrationActions.removeState),
        tap(() => {
          if (isPlatformBrowser(this.platformId)) {
            this.storage.removeItem(this.primaryKey)
          }
        }),
      ),
    { dispatch: false },
  )

  ngrxOnInitEffects(): Action {
    console.info('dispatch action to hydrate')
    return HydrationActions.hydrate()
  }

  /**
   *
   * @todo: for cms it is ok to use galabid_dashboard as the local de key
   *        but for the presenter, it could use the event url as the app does
   */
  getLocalDBKey(): string {
    const host = window.location.hostname

    const pathNameKey = window.location.pathname.trim().split('/')[1]

    // front

    if (host && this.APP_URLS.includes(host) && pathNameKey !== 'campaign') {
      if (pathNameKey) {
        return pathNameKey
      } else {
        return window.location.hash.trim().split('/')[1]
      }
    }

    // dashboard

    if (host && this.CMS_URLS.includes(host)) {
      const auctionUrlName: string = window.location.pathname.trim().split('/')[2]
      return `${auctionUrlName}-dashboard`
    }

    // presenter (TODO)

    if (host && this.PRESENTER_URLS.includes(host)) {
      return 'presenter'
    }
    return ''
  }
}
